import { toast } from "sonner";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "@/components/header/Header"; 
import { Accordion } from "@/components/ui/accordion";
import ConsentAppCard from "@/components/consent/ConsentAppCard";

import { genericService } from "@/services/GenericService";

import { tryCatch } from "@/utils/try-catch";

import { ConsentAnswerRequest } from "@/models/shared/consents/ConsentAuthorizationRequest";
import {
  AppConsents,
  UpdateUserConsentsRequest,
  UserConsentsResponse,
} from "@/models/site-credentials/consents/UserAppConsents";

/** Aplica localmente a decisao submetida, para o card refletir o novo estado sem recarregar. */
const applyAnswers = (app: AppConsents, answers: Array<ConsentAnswerRequest>): AppConsents => {
  const decidedAt = new Date().toISOString();

  return {
    ...app,
    items: app.items.map((item) => {
      const answer = answers.find((a) => a.id === item.id);

      return answer == null
        ? item
        : { ...item, state: answer.answer ? "Accepted" : "Refused", decisionDate: decidedAt };
    }),
  };
};

export default function ConsentListPage() {
  const { t } = useTranslation(); 
  const location = useLocation<UserConsentsResponse>();

  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState<Array<AppConsents>>(location.state?.properties?.apps ?? []);

  const nextStep = location.state?.nextStep ?? "/api" + location.pathname;
  const isDeepLink = location.state?.from === "dl";

  const handleSubmit = async (app: AppConsents, answers: Array<ConsentAnswerRequest>) => {
    setLoading(true);

    // O ecra e multi-app: o clientId da app alvo tem de ir no payload, porque o backend nao o
    // consegue inferir da sessao (o ClientId da sessao e o cliente de login).
    const { data: response, error } = await tryCatch(
      genericService.post<UserConsentsResponse, UpdateUserConsentsRequest>(nextStep, {
        clientId: app.clientId,
        consents: answers,
      })
    );

    if (response?.kind === "rate_limited") {
      toast.error(t("site_credentials.consents.rate_limited"));
      setLoading(false);
      return;
    }

    if (error || response?.kind !== "success") {
      toast.error(t("common.unexpected_error"));
      setLoading(false);
      return;
    }

    const refreshed = response.value?.properties?.apps;

    setApps((prev) =>
      refreshed ?? prev.map((a) => (a.clientId === app.clientId ? applyAnswers(a, answers) : a))
    );

    toast.success(t("site_credentials.consents.save_success"));
    setLoading(false);
  };

  return (
    <div className="flex w-full flex-col gap-10 pb-2">
      <Header
        title={t("site_credentials.consents.title")}
        subtitle={t("site_credentials.consents.subtitle")}
        showLoading={loading}
        showBackButton={!isDeepLink}
        backClickToPushPath="/site-credentials"
      />

      {apps.length > 0 ? (
        <Accordion type="single" collapsible className="flex w-full flex-col gap-4">
          {apps.map((app) => (
            <ConsentAppCard
              key={`${app.clientId}-${app.items.map((i) => `${i.id}:${i.state}`).join("|")}`}
              app={app}
              loading={loading}
              onSubmit={handleSubmit}
            />
          ))}
        </Accordion>
      ) : (
        <span className="text-base text-[var(--on-surface-variant)]">
          {t("site_credentials.consents.empty")}
        </span>
      )}
    </div>
  );
}
